import { useRef, useState } from "react";

import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import { Popover, Slider } from "antd";
import styled from "styled-components";

import { BaseButton } from "components/base";
import ColorChooser from "components/base/ColorChooser";
import { ISeriesStyle } from "components/multiphase-chart/models/shared.models";

interface ColorEditorModel {
  color: string;
  dashed?: boolean;
  defaultStyle?: ISeriesStyle & { opacity?: number };
  disableTransparent?: boolean;
  isDisabled?: boolean;
  onClose?: () => void;
  onChange: (color: string, thickness: number) => void;
  opacity?: {
    value: number;
    onChange: (opacity: number) => void;
  };
  showDashed?: boolean;
  thickness: number;
  width?: number;
  squareDisplay?: boolean;
}

interface PopoverTriggerModel {
  color: string;
  thickness: number;
  width?: number;
  squareDisplay: boolean;
}
function PopoverTrigger({
  color,
  thickness,
  width = 40,
  squareDisplay = false
}: PopoverTriggerModel) {
  return (
    <ColorDiv
      data-testid="color-div"
      color={color}
      thickness={thickness}
      width={width}
      squareDisplay={squareDisplay}
    />
  );
}

const ColorDiv = styled.div<{
  color: string;
  thickness: number;
  width?: number;
  squareDisplay: boolean;
}>`
  width: ${(props) =>
    props.squareDisplay ? "18px" : props.width ? `${props.width}px` : "40px"};
  height: ${(props) => (props.squareDisplay ? `18px` : `${2 * props.thickness}px`)};
  background-color: ${(props) => props.color};

  border-radius: 2px;
  transition: border-radius, box-shadow, var(--duration-short) var(--ease);

  &:hover,
  &:focus,
  &:active {
    outline: rgba(var(--color-shadow-rgb), 0.06);
    box-shadow: rgba(var(--color-shadow-rgb), 0.1) 0 1px 2px,
      rgba(var(--color-shadow-rgb), 0.1) 0 0 0 1px, var(--shadow-focus);
  }
`;

const ColorEditor: React.FC<ColorEditorModel> = ({
  children,
  color,
  defaultStyle,
  disableTransparent = false,
  isDisabled,
  onClose,
  onChange,
  opacity,
  thickness,
  width,
  squareDisplay,
  ...props
}) => {
  const [customColors, setCustomColors] = useState([]);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const wasOpenRef = useRef(false);

  return (
    <Popover
      placement="right"
      trigger="click"
      // Named so the guided walkthrough can wait for this picker to open.
      overlayClassName="color-editor-popover"
      open={isPopoverVisible}
      onOpenChange={(visible) => {
        if (isDisabled) return;

        setIsPopoverVisible(visible);
        const wasOpen = wasOpenRef.current;
        wasOpenRef.current = visible;
        if (!visible && wasOpen) {
          onClose?.();
        }
      }}
      content={
        <div
          onClick={(e) => {
            // Ensure the parents event isn't triggered
            // e.g Product button click event being triggered
            e.preventDefault();
            e.stopPropagation();
          }}>
          <ColorChooser
            selectedColor={color}
            onChange={(c) => onChange && onChange(c, thickness)}
            customColors={customColors}
            setCustomColors={setCustomColors}
            disableTransparent={disableTransparent}
          />
          Thickness
          <Slider
            min={1}
            max={20}
            step={1}
            value={thickness}
            onChange={(thickness) => onChange && onChange(color, thickness)}></Slider>
          {opacity && (
            <>
              Opacity:
              <Slider
                min={0}
                max={1}
                step={0.1}
                value={opacity.value}
                onChange={(opacityValue) => opacity.onChange(opacityValue)}
              />
            </>
          )}
          {defaultStyle && (
            <RestoreDefaultWrapper>
              <div>Restore Default</div>
              <ResetButton
                appearance="subtle"
                onClick={() => {
                  onChange && onChange(defaultStyle.color, defaultStyle.width);
                  if (opacity && defaultStyle.opacity !== undefined) {
                    opacity.onChange(defaultStyle.opacity);
                  }
                }}>
                <SettingsBackupRestoreIcon
                  style={{ fontSize: "18px" }}></SettingsBackupRestoreIcon>
              </ResetButton>
            </RestoreDefaultWrapper>
          )}
        </div>
      }>
      {children || (
        <PopoverWrapper {...props} disabled={isDisabled}>
          <PopoverTrigger
            color={color}
            thickness={thickness}
            width={width}
            squareDisplay={squareDisplay}
          />
        </PopoverWrapper>
      )}
    </Popover>
  );
};

export default ColorEditor;

const PopoverWrapper = styled.div`
  min-height: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
`;

const RestoreDefaultWrapper = styled.div`
  width: 100%;
  display: inline-flex;
  justify-content: space-between;
  cursor: pointer;
  margin-left: auto;
`;

const ResetButton = styled(BaseButton)`
  height: auto;
  padding-left: 0px;
  padding-right: 0px;
`;
